import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Send, ShieldCheck } from "lucide-react";
import NetworkCanvas from "@/components/NetworkCanvas";
import { Reveal, ease } from "@/components/Reveal";
import { COMPANY } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) next.email = "Please enter a valid email address.";
    if (!form.message.trim()) next.message = "Please enter your message.";
    return next;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — your enquiry has been received.", {
        description: "We will get back to you shortly.",
      });
      setForm({ name: "", email: "", company: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  const inputClass = (field) =>
    `w-full rounded-xl border bg-white px-4 py-3.5 text-[15px] text-ink placeholder:text-steel/50 outline-none transition-colors duration-200 focus:border-iris focus:ring-2 focus:ring-iris/20 ${
      errors[field] ? "border-red-400" : "border-hairline"
    }`;

  return (
    <main>
      {/* Hero */}
      <section className="noise relative overflow-hidden bg-midnight" data-testid="contact-hero">
        <NetworkCanvas className="absolute inset-0 h-full w-full opacity-40" density={30000} interactive={false} />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-midnight/60 via-transparent to-midnight" />
        <div className="relative mx-auto w-full max-w-7xl px-6 pb-24 pt-40 lg:px-8 lg:pb-32 lg:pt-48">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease }}
            className="flex items-center gap-4"
          >
            <span className="h-px w-12 bg-gradient-to-r from-iris to-aqua" />
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-aqua">Contact Us</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.12, ease }}
            className="mt-8 font-display text-4xl font-extrabold leading-[1.05] tracking-tighter text-white sm:text-5xl lg:text-6xl"
            data-testid="contact-hero-title"
          >
            Talk to <span className="text-gradient">Us</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25, ease }}
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
            data-testid="contact-hero-tagline"
          >
            {COMPANY.tagline}
          </motion.p>
        </div>
      </section>

      {/* Form + details */}
      <section className="bg-offwhite" data-testid="contact-form-section">
        <div className="mx-auto grid w-full max-w-7xl gap-10 px-6 py-24 lg:grid-cols-5 lg:gap-14 lg:px-8 lg:py-32">
          <Reveal className="lg:col-span-3">
            <form
              noValidate
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="rounded-2xl border border-hairline bg-white p-8 shadow-[0_8px_30px_rgba(17,20,43,0.05)] lg:p-10"
            >
              <h2 className="font-display text-2xl font-bold tracking-tight text-navy">Send an Enquiry</h2>
              <p className="mt-2 text-sm leading-relaxed text-steel">
                Fill in the form and our team will respond to your request.
              </p>
              <div className="mt-8 grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-navy">
                    Name *
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-input-name"
                    className={inputClass("name")}
                    placeholder="Your full name"
                    value={form.name}
                    onChange={update("name")}
                  />
                  {errors.name && (
                    <p className="mt-2 text-xs font-semibold text-red-500" data-testid="contact-error-name">
                      {errors.name}
                    </p>
                  )}
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-navy">
                    Email *
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    data-testid="contact-input-email"
                    className={inputClass("email")}
                    placeholder="you@company.com"
                    value={form.email}
                    onChange={update("email")}
                  />
                  {errors.email && (
                    <p className="mt-2 text-xs font-semibold text-red-500" data-testid="contact-error-email">
                      {errors.email}
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-5">
                <label htmlFor="contact-company" className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-navy">
                  Company
                </label>
                <input
                  id="contact-company"
                  data-testid="contact-input-company"
                  className={inputClass("company")}
                  placeholder="Your organization (optional)"
                  value={form.company}
                  onChange={update("company")}
                />
              </div>
              <div className="mt-5">
                <label htmlFor="contact-message" className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-navy">
                  Message *
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-input-message"
                  rows={5}
                  className={`${inputClass("message")} resize-y`}
                  placeholder="Tell us about your security needs"
                  value={form.message}
                  onChange={update("message")}
                />
                {errors.message && (
                  <p className="mt-2 text-xs font-semibold text-red-500" data-testid="contact-error-message">
                    {errors.message}
                  </p>
                )}
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-form-submit-button"
                className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-iris px-8 py-4 font-display text-sm font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-aqua hover:text-midnight disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {sending ? "Sending…" : "Send Enquiry"}
                <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-2">
            <div
              className="noise flex h-full flex-col rounded-2xl border border-white/10 bg-gradient-to-b from-midnight to-navy-light p-8 lg:p-10"
              data-testid="contact-details-card"
            >
              <h2 className="font-display text-2xl font-bold tracking-tight text-white">Reach Us Directly</h2>
              <div className="mt-8 space-y-4">
                <a
                  href={`mailto:${COMPANY.email}`}
                  data-testid="contact-details-email"
                  className="group flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 p-4 transition-colors duration-300 hover:border-aqua/50"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-iris/20 text-aqua">
                    <Mail className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-xs font-bold uppercase tracking-[0.2em] text-white/40">Email</span>
                    <span className="mt-0.5 block font-display text-[15px] font-bold text-white transition-colors group-hover:text-aqua">
                      {COMPANY.email}
                    </span>
                  </span>
                </a>
                <a
                  href={COMPANY.phoneHref}
                  data-testid="contact-details-phone"
                  className="group flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 p-4 transition-colors duration-300 hover:border-aqua/50"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-iris/20 text-aqua">
                    <Phone className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-xs font-bold uppercase tracking-[0.2em] text-white/40">Phone</span>
                    <span className="mt-0.5 block font-display text-[15px] font-bold text-white transition-colors group-hover:text-aqua">
                      {COMPANY.phoneDisplay}
                    </span>
                  </span>
                </a>
              </div>

              <div className="mt-10 border-t border-white/10 pt-8">
                <span className="flex items-center gap-3">
                  <ShieldCheck className="h-5 w-5 text-aqua" />
                  <span className="font-display text-base font-extrabold tracking-[0.18em] text-white">
                    {COMPANY.legalName}
                  </span>
                </span>
                <p className="mt-4 text-sm leading-relaxed text-white/55" data-testid="contact-details-company">
                  {COMPANY.incorporation} {COMPANY.tagline}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
